import { Spinner } from '@/components/glitch-ui/spinner';
import { useState, useMemo, useCallback, useRef, forwardRef, useImperativeHandle } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAtomValue } from 'jotai';
import { ColumnDef } from '@tanstack/react-table';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Trash2, AlertCircle, Link as LinkIcon, Pencil } from 'lucide-react';
import { useToast } from '@/hooks/useToast';
import {
  createAccountSitelink,
  updateAccountSitelink,
  deleteAccountSitelink,
} from '@/lib/apis';
import { accountSitelinksQueryOptions } from '@/lib/query-options';
import type { AccountSitelink } from '@/lib/types';
import { NoGoogleAdsAccountSelected } from '@/components/glitch-ui/no-google-ads-account';
import { DataTable } from '@/components/glitch-ui/data-table';
import { IconButton } from '@/components/glitch-ui/icon-button';
import { ConformationModal } from '@/components/glitch-ui/confirmation-modal';
import { FieldError, InputAdornment } from '@/components/glitch-ui/field-error';
import { CharacterCounter } from '@/components/ui/character-counter';
import { BusinessLandingPageCombobox } from '@/components/glitch-ui/business-landing-page-combobox';
import { googleAdsAccountAtom } from '@/store';


interface ModalState {
  isOpen: boolean;
  editing: AccountSitelink | null;
  name: string;
  url: string;
  desc1: string;
  desc2: string;
}

const emptyModal: ModalState = {
  isOpen: false,
  editing: null,
  name: '',
  url: '',
  desc1: '',
  desc2: '',
};

export interface AccountSitelinksRef {
  openAdd: () => void;
}

export const AccountSitelinks = forwardRef<AccountSitelinksRef>((_props, ref) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const googleAdsAccount = useAtomValue(googleAdsAccountAtom);
  const [modal, setModal] = useState<ModalState>(emptyModal);
  // Tell-on-click (#5857): submit stays enabled; clicking marks missing/invalid fields.
  type SitelinkFieldError = Partial<Record<'name' | 'url' | 'descPair', string>>;
  const [fieldErrors, setFieldErrors] = useState<SitelinkFieldError>({});
  const [sitelinkToDelete, setSitelinkToDelete] = useState<AccountSitelink | null>(null);

  const {
    data: sitelinksData,
    isLoading,
    isError,
  } = useQuery(accountSitelinksQueryOptions(googleAdsAccount?.googleads_account_id));

  const createMutation = useMutation({
    mutationFn: (data: { name: string; url: string; description1: string; description2: string }) =>
      createAccountSitelink(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['account-sitelinks'] });
      setModal(emptyModal);
      toast({
        title: 'Sitelink created',
        description: 'Account-level sitelink has been created and will apply to all campaigns.',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Failed to create sitelink',
        description: error.message || 'Please try again later.',
        variant: 'destructive',
      });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({
      uuid,
      data,
    }: {
      uuid: string;
      data: { name: string; url: string; description1: string; description2: string };
    }) => updateAccountSitelink(uuid, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['account-sitelinks'] });
      setModal(emptyModal);
      toast({
        title: 'Sitelink updated',
        description: 'Account-level sitelink has been updated.',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Failed to update sitelink',
        description: error.message || 'Please try again later.',
        variant: 'destructive',
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (uuid: string) => deleteAccountSitelink(uuid),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['account-sitelinks'] });
      setSitelinkToDelete(null);
    },
    onError: (error: Error) => {
      toast({
        title: 'Failed to remove sitelink',
        description: error.message || 'Please try again later.',
        variant: 'destructive',
      });
    },
  });

  const openAddModal = () => {
    setModal({ ...emptyModal, isOpen: true });
    setFieldErrors({});
  };

  const openEditModal = useCallback((sitelink: AccountSitelink) => {
    setModal({
      isOpen: true,
      editing: sitelink,
      name: sitelink.name,
      url: sitelink.url,
      desc1: sitelink.description1,
      desc2: sitelink.description2,
    });
    setFieldErrors({});
  }, []);

  const handleSave = () => {
    const errors: SitelinkFieldError = {};
    if (!modal.name.trim()) errors.name = 'Required';
    if (!modal.url.trim()) errors.url = 'Required';
    if (Boolean(modal.desc1.trim()) !== Boolean(modal.desc2.trim())) errors.descPair = 'Provide both or neither';
    if (Object.values(errors).some(Boolean)) {
      setFieldErrors(errors);
      return;
    }

    const data = {
      name: modal.name.trim(),
      url: modal.url,
      description1: modal.desc1.trim(),
      description2: modal.desc2.trim(),
    };

    if (modal.editing) {
      updateMutation.mutate({ uuid: modal.editing.uuid, data });
    } else {
      createMutation.mutate(data);
    }
  };

  const handleConfirmDelete = () => {
    if (sitelinkToDelete) {
      deleteMutation.mutate(sitelinkToDelete.uuid);
    }
  };

  const openEditModalRef = useRef(openEditModal);
  openEditModalRef.current = openEditModal;

  const sitelinkCount = sitelinksData?.sitelinks?.length ?? 0;

  const columns = useMemo((): ColumnDef<AccountSitelink>[] => [
    {
      accessorKey: 'name',
      header: `Sitelinks (${sitelinkCount}/20)`,
      meta: { className: 'px-6 w-[35%] whitespace-normal' },
      cell: ({ row }) => (
        <div className="flex items-center gap-2">
          <LinkIcon className="h-3.5 w-3.5 text-gray-400 shrink-0" />
          <div>
            <div className="text-sm font-medium text-gray-900">{row.original.name}</div>
            <div className="text-xs text-gray-500">{row.original.url}</div>
          </div>
        </div>
      ),
    },
    {
      accessorKey: 'description1',
      header: 'Description',
      meta: { className: 'px-6 w-[40%] whitespace-normal' },
      cell: ({ row }) => {
        const { description1, description2 } = row.original;
        if (!description1 && !description2) {
          return <span className="text-sm text-gray-900">-</span>;
        }
        return (
          <div>
            <div className="text-sm text-gray-900">{description1}</div>
            <div className="text-sm text-gray-900">{description2}</div>
          </div>
        );
      },
    },
    {
      id: 'source',
      header: 'Source',
      enableSorting: false,
      meta: { className: 'px-6 w-[15%]' },
      // Assets on this page are all account-level; the pill mirrors the campaign
      // assets tab (where it distinguishes Account vs Campaign) for visual parity.
      cell: () => (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-600">
          Account
        </span>
      ),
    },
    {
      id: 'actions',
      enableSorting: false,
      cell: ({ row }) => (
        <div className="flex justify-end gap-1">
          <IconButton
            variant="default"
            aria-label="Edit sitelink"
            onClick={() => openEditModalRef.current(row.original)}
          >
            <Pencil className="h-4 w-4" aria-hidden="true" />
          </IconButton>
          <IconButton
            variant="destructive"
            aria-label="Delete sitelink"
            onClick={() => setSitelinkToDelete(row.original)}
          >
            <Trash2 className="h-4 w-4" aria-hidden="true" />
          </IconButton>
        </div>
      ),
      meta: { className: 'px-6 w-[10%] text-right' },
    },
  ], [sitelinkCount]);

  useImperativeHandle(ref, () => ({ openAdd: openAddModal }), [openAddModal]);

  if (!googleAdsAccount?.googleads_account_id) {
    return (
      <NoGoogleAdsAccountSelected description="A Google Ads account needs to be selected for this organization before account sitelinks can be managed." />
    );
  }

  const sitelinks = sitelinksData?.sitelinks || [];

  if (isError) {
    return (
      <div className="bg-white shadow-sm border border-red-200 rounded-lg overflow-hidden">
        <div className="p-6 flex items-center gap-3 text-red-600">
          <AlertCircle className="h-5 w-5" />
          <span className="text-sm">Failed to load account sitelinks</span>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="border rounded-lg bg-white overflow-hidden">
        <div className="flex items-center justify-center gap-3 py-16">
          <Spinner size="md" className="text-gray-500" />
          <span className="text-gray-500">Loading account sitelinks...</span>
        </div>
      </div>
    );
  }

  const isPending = createMutation.isPending || updateMutation.isPending;
  const descPaired = Boolean(modal.desc1.trim()) === Boolean(modal.desc2.trim());

  return (
    <div>
      {/* Table */}
      <DataTable
        data={sitelinks}
        columns={columns}
        variant="bordered"
        pageSize={999}
        emptyIcon={LinkIcon}
        emptyTitle="No Account Sitelinks"
        emptyDescription="Create your first account sitelink to apply it to all campaigns by default."
      />

      {/* Add/Edit Dialog */}
      <Dialog open={modal.isOpen} onOpenChange={(open) => !open && setModal(emptyModal)}>
        <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto" aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle>{modal.editing ? 'Edit Sitelink' : 'Add Account Sitelink'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <div className="flex items-center justify-between">
                <Label>Name</Label>
                <FieldError message={fieldErrors.name} />
              </div>
              <div className="relative mt-1">
                <Input
                  value={modal.name}
                  onChange={(e) => { setModal((prev) => ({ ...prev, name: e.target.value })); setFieldErrors((prev) => ({ ...prev, name: undefined })); }}
                  maxLength={25}
                  placeholder="e.g. Contact Us"
                  autoFocus
                  error={!!fieldErrors.name}
                  className={fieldErrors.name ? 'pr-20' : 'pr-14'}
                />
                <InputAdornment error={!!fieldErrors.name}>
                  <CharacterCounter current={modal.name.length} max={25} variant="compact" />
                </InputAdornment>
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label>URL</Label>
                <FieldError message={fieldErrors.url} />
              </div>
              <div className={fieldErrors.url ? 'relative mt-1 rounded-md ring-1 ring-red-500' : 'relative mt-1'}>
                <BusinessLandingPageCombobox
                  selectedUrl={modal.url || null}
                  onSelect={(entry) => { setModal((prev) => ({ ...prev, url: entry?.url ?? '' })); setFieldErrors((prev) => ({ ...prev, url: undefined })); }}
                  placeholder="Select or add a landing page URL..."
                />
                <InputAdornment error={!!fieldErrors.url} />
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label>Description Line 1</Label>
                {!modal.desc1.trim() && <FieldError message={fieldErrors.descPair} />}
              </div>
              <div className="relative mt-1">
                <Input
                  value={modal.desc1}
                  onChange={(e) => { setModal((prev) => ({ ...prev, desc1: e.target.value })); setFieldErrors((prev) => ({ ...prev, descPair: undefined })); }}
                  maxLength={35}
                  placeholder="Optional (must be paired with line 2)"
                  error={!!fieldErrors.descPair && !modal.desc1.trim()}
                  className={fieldErrors.descPair && !modal.desc1.trim() ? 'pr-20' : 'pr-14'}
                />
                <InputAdornment error={!!fieldErrors.descPair && !modal.desc1.trim()}>
                  <CharacterCounter current={modal.desc1.length} max={35} variant="compact" />
                </InputAdornment>
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label>Description Line 2</Label>
                {!modal.desc2.trim() && <FieldError message={fieldErrors.descPair} />}
              </div>
              <div className="relative mt-1">
                <Input
                  value={modal.desc2}
                  onChange={(e) => { setModal((prev) => ({ ...prev, desc2: e.target.value })); setFieldErrors((prev) => ({ ...prev, descPair: undefined })); }}
                  maxLength={35}
                  placeholder="Optional (must be paired with line 1)"
                  error={!!fieldErrors.descPair && !modal.desc2.trim()}
                  className={fieldErrors.descPair && !modal.desc2.trim() ? 'pr-20' : 'pr-14'}
                />
                <InputAdornment error={!!fieldErrors.descPair && !modal.desc2.trim()}>
                  <CharacterCounter current={modal.desc2.length} max={35} variant="compact" />
                </InputAdornment>
              </div>
            </div>
            {!descPaired && (
              <p className="text-xs text-red-500">
                Provide both description lines or leave both blank. Google Ads requires them as a pair.
              </p>
            )}
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setModal(emptyModal)}
              disabled={isPending}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleSave}
              disabled={isPending}
              className="bg-[#290BD9] hover:bg-[#2309C5] text-white"
            >
              {isPending ? 'Saving...' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <ConformationModal
        isOpen={!!sitelinkToDelete}
        onClose={() => setSitelinkToDelete(null)}
        onConfirm={handleConfirmDelete}
        title="Remove Account Sitelink"
        description={`Are you sure you want to remove "${sitelinkToDelete?.name}"? This will remove it from all campaigns that inherit it.`}
        confirmText="Remove"
        cancelText="Cancel"
        isLoading={deleteMutation.isPending}
      />
    </div>
  );
});
AccountSitelinks.displayName = 'AccountSitelinks';
